import React, { useState, ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { BackendResponse } from '../types';
import { useI18n } from '../hooks/useI18n';
import {
  Mail,
  CheckCircle,
  AlertCircle,
  Leaf,
  ArrowLeft,
  Lock
} from 'lucide-react';
import LanguageSwitcher from "../components/LanguageSwitcher";

const ForgotPasswordPage: React.FC = () => {
  const { t, isRTL } = useI18n();
  const [email, setEmail] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [error, setError] = useState<string>('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response: BackendResponse = await authAPI.forgotPassword(email);
      setMessage(response.message || t('forgotPassword.successMessage'));
    } catch (error: unknown) {
      const apiError = error as any;
      setError(apiError.response?.data?.message || t('forgotPassword.errorMessage'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex items-center justify-center w-full">
          <div className="max-w-md text-center text-white px-8">
            <Link to={"/"} className="flex items-center justify-center mb-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mr-4">
                <img src="/solvingers.svg" />
              </div>
              <h1 className="text-4xl font-bold">Facturelik</h1>
            </Link>

            <h2 className="text-3xl font-semibold mb-4">{t('auth.forgotPassword.title')}</h2>
            <p className="text-lg text-white/90 mb-12">
              {t('auth.forgotPassword.subtitle')}
            </p>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.forgotPassword.emailResetLink')}</h3>
                  <p className="text-white/80">{t('auth.forgotPassword.emailResetDescription')}</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold mb-2">{t('auth.forgotPassword.secureProcess')}</h3>
                  <p className="text-white/80">{t('auth.forgotPassword.secureProcessDescription')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden flex items-center justify-center mb-8">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-3">
              <img src="/solvingers.svg" />
            </div>
            <h1 className="text-2xl font-bold text-secondary-900">Facturelik</h1>
          </Link>

          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-secondary-900 mb-2">{t('auth.forgotPassword.forgotPassword')}</h2>
            <p className='text-secondary-600'>{t('auth.forgotPassword.enterEmailDescription')}</p>
          </div>

          {message && (
            <div className={`mb-6 p-4 bg-success-50 border border-success-200 rounded-lg flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
              <CheckCircle className="w-5 h-5 text-success-600 flex-shrink-0" />
              <p className="text-success-800">{message}</p>
            </div>
          )}

          {error && (
            <div className={`mb-6 p-4 bg-error-50 border border-error-200 rounded-lg flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-3'}`}>
              <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
              <p className="text-error-800">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="form-label">
                {t('auth.forgotPassword.emailAddress')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-secondary-400" />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={handleChange}
                  placeholder={t('auth.forgotPassword.enterEmailPlaceholder')}
                  className="form-input pl-10"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 spinner"></div>
                  <span>{t('auth.forgotPassword.sending')}</span>
                </div>
              ) : (
                t('auth.forgotPassword.sendResetLink')
              )}
            </button>
          </form>

          <div className={`mt-8 text-center ${isRTL ? 'space-y-reverse' : 'space-y-4'}`}>
            <Link
              to="/login"
              className={`inline-flex items-center ${isRTL ? 'space-x-reverse' : 'space-x-2'} text-primary-600 hover:text-primary-700 font-medium`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t('auth.forgotPassword.backToLogin')}</span>
            </Link>

            <div className="text-secondary-600">
              <p>
                {t('auth.forgotPassword.noAccount')}{' '}
                <Link to="/signup" className="text-primary-600 hover:text-primary-700 font-semibold">
                  {t('auth.forgotPassword.signUp')}
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
